import * as React from "react";

import type {
  WpxTableFeature,
  WpxTableFeatureConfig,
  WpxTablePropsClassNames,
  WpxTableUiConfig,
} from "../types/dataTable.types";
import { resolveWpxTableFeatureConfig } from "../utils/wpxTable.variants";

import { useMinLoadingDuration } from "@/hooks/state/useMinLoadingDuration";

interface WpxTableUiResolutionParams {
  classNames?: WpxTablePropsClassNames;
  enableColumnResizing?: boolean;
  feature?: WpxTableFeature;
  filterClearButtonSize?: "sm" | "lg";
  hideFilters?: boolean;
  hideFunctions?: boolean;
  isLoading?: boolean;
  showSelectedCount?: boolean;
  showTitle?: boolean;
  showTotalCount?: boolean;
  toolbarHide?: boolean;
  ui?: WpxTableUiConfig;
}

interface ResolvedUiConfig {
  classNames?: WpxTablePropsClassNames;
  clearButtonSize?: "sm" | "lg";
  enableColumnResizing: boolean;
  hideFilters: boolean;
  hideToolbar: boolean;
  isLoading: boolean;
  showSelectedCount: boolean;
  showTitle: boolean;
  showTotalCount: boolean;
}

interface WpxTableUiResolutionReturn {
  featureConfig: WpxTableFeatureConfig;
  isLoading: boolean;
  resolvedUi: ResolvedUiConfig;
}

/**
 * Resolves the final UI configuration by merging the `ui` prop, individual
 * legacy props, feature defaults, and the `hide` config into one flat shape.
 *
 * @param {WpxTableUiResolutionParams} params - Raw UI-related props from the consumer.
 * @returns {WpxTableUiResolutionReturn} Merged UI config with a debounced loading flag.
 */
export const useWpxTableUiResolution = (
  params: WpxTableUiResolutionParams,
): WpxTableUiResolutionReturn => {
  const {
    classNames,
    enableColumnResizing = false,
    feature,
    filterClearButtonSize,
    hideFilters,
    hideFunctions,
    isLoading: isLoadingProp = false,
    showSelectedCount,
    showTitle = false,
    showTotalCount,
    toolbarHide,
    ui,
  } = params;

  const featureConfig = React.useMemo(() => {
    return resolveWpxTableFeatureConfig(feature);
  }, [feature]);

  const resolvedUi = React.useMemo((): ResolvedUiConfig => {
    return {
      classNames: ui?.classNames ?? classNames,
      clearButtonSize: ui?.clearButtonSize ?? filterClearButtonSize,
      enableColumnResizing: ui?.enableColumnResizing ?? enableColumnResizing,
      hideFilters: ui?.hideFilters ?? hideFilters ?? false,
      hideToolbar: ui?.hideToolbar ?? hideFunctions ?? toolbarHide ?? false,
      isLoading: ui?.isLoading ?? isLoadingProp,
      showTitle: ui?.showTitle ?? showTitle,
      showSelectedCount:
        ui?.showSelectedCount ??
        showSelectedCount ??
        featureConfig.showSelectedCount,
      showTotalCount:
        ui?.showTotalCount ?? showTotalCount ?? featureConfig.showTotalCount,
    };
  }, [
    classNames,
    enableColumnResizing,
    featureConfig.showSelectedCount,
    featureConfig.showTotalCount,
    filterClearButtonSize,
    hideFilters,
    hideFunctions,
    isLoadingProp,
    showSelectedCount,
    showTitle,
    showTotalCount,
    toolbarHide,
    ui?.classNames,
    ui?.clearButtonSize,
    ui?.enableColumnResizing,
    ui?.hideFilters,
    ui?.hideToolbar,
    ui?.isLoading,
    ui?.showSelectedCount,
    ui?.showTitle,
    ui?.showTotalCount,
  ]);

  const isLoading = useMinLoadingDuration(resolvedUi.isLoading);

  return {
    featureConfig,
    isLoading,
    resolvedUi,
  };
};
